import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Brush, Eraser, Hand, Check, X, Plus, Minus } from 'lucide-react';
import { canvasToBinaryMask } from '../lib/mask-morphology';
import {
  smoothMask,
  shiftEdge,
  contrastMaskCanvas,
  smartRadiusFeather,
  featherMaskToCanvas,
} from '../lib/edge-refinement';

/** Refine Edge full-screen workspace — Tier 1 Group D.
 *
 *  Refines an input selection mask via:
 *    - Smart Radius / Radius (edge-band feather)
 *    - Smooth (binary majority pass)
 *    - Feather (Gaussian alpha blur)
 *    - Contrast (alpha S-curve)
 *    - Shift Edge (dilate/erode)
 *
 *  Plus mini-palette brushes for adding to / subtracting from the
 *  base mask (Quick Select brush adds, Refine Edge brush smartly
 *  detects edges — in v1 we ship "add" and "subtract" only; the
 *  smart-radius-driven refinement happens via the slider rather
 *  than per-stroke).
 *
 *  Output: Selection (default). Mask / New Layer / New Layer with
 *  Mask are listed in the roadmap; UI shows them but disabled in
 *  v1 because each needs layer-tree wiring beyond this PR's scope
 *  (and the roadmap explicitly lets us ship incremental Tier 1
 *  groups).
 */
export type RefineEdgeOutput = 'selection' | 'mask' | 'new-layer' | 'new-layer-with-mask';

export type RefineEdgeViewMode =
  | 'on-black'
  | 'on-white'
  | 'overlay'
  | 'b-and-w'
  | 'on-layers'
  | 'marching-ants';

interface RefineEdgeWorkspaceProps {
  /** Source canvas to composite the matte preview against. Captured
   *  at modal-open time. Passed as `HTMLCanvasElement` (not
   *  `ImageData`) so the workspace can `drawImage` it cheaply
   *  instead of paying multi-MB `putImageData` costs on every
   *  preview re-render — see ColorRangeDialog's comment for the
   *  full perf rationale. */
  source: HTMLCanvasElement;
  /** Initial mask to refine. Null = empty mask (the user can use
   *  the mini-palette brushes to paint a selection from scratch). */
  initialMaskCanvas: HTMLCanvasElement | null;
  onClose: () => void;
  /** Apply — `outputTo` is the destination chosen by the user in
   *  the bottom panel. Always passes the refined alpha-mask canvas
   *  sized to the source dims; caller routes it to the right
   *  destination. */
  onApply: (maskCanvas: HTMLCanvasElement, outputTo: RefineEdgeOutput) => void;
}

type Tool = 'add' | 'subtract' | 'hand';

const VIEW_MODES: { value: RefineEdgeViewMode; label: string }[] = [
  { value: 'on-black', label: 'On Black' },
  { value: 'on-white', label: 'On White' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'b-and-w', label: 'B&W' },
  { value: 'on-layers', label: 'On Layers' },
  { value: 'marching-ants', label: 'Marching Ants' },
];

const PREVIEW_MAX = 720;

export function RefineEdgeWorkspace({
  source,
  initialMaskCanvas,
  onClose,
  onApply,
}: RefineEdgeWorkspaceProps) {
  // Refinement parameters. Defaults match Photoshop's "open with
  // current edge" — no refinement until the user moves a slider.
  const [radius, setRadius] = useState(0);
  const [smartRadius, setSmartRadius] = useState(false);
  const [smooth, setSmooth] = useState(0);
  const [feather, setFeather] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [shift, setShift] = useState(0);
  const [view, setView] = useState<RefineEdgeViewMode>('overlay');
  const [tool, setTool] = useState<Tool>('add');
  const [brushSize, setBrushSize] = useState(30);
  const [output, setOutput] = useState<RefineEdgeOutput>('selection');

  // Working canvases.
  const baseBinaryRef = useRef<Uint8Array | null>(null); // user-edited mask in binary form
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Animation phase for the marching-ants view mode. Shifts the
  // stipple offset every ~120ms so the dotted boundary visibly
  // marches along the selection edge. Throttled to avoid burning
  // CPU at 60Hz when 8-12Hz is plenty for "ants are moving" feel.
  const [antsPhase, setAntsPhase] = useState(0);
  useEffect(() => {
    if (view !== 'marching-ants') return;
    const id = window.setInterval(() => setAntsPhase((p) => (p + 1) & 0xff), 120);
    return () => window.clearInterval(id);
  }, [view]);

  // Brush-stroke recompute ticker. Increments on every stroke dab
  // so the recompute effect (whose deps include `recomputeTick`)
  // re-fires and re-renders the preview from the freshly-painted
  // base mask. Without this dep, paint dabs mutated
  // `baseBinaryRef.current` invisibly until the next unrelated
  // state change (slider or view-mode tab).
  const [recomputeTick, setRecomputeTick] = useState(0);
  // Per-stroke brush state for the mini-palette tools.
  const strokeRef = useRef<{
    lastX: number;
    lastY: number;
  } | null>(null);

  // Cursor position over the preview area, in DOM pixels relative
  // to the viewport wrapper. Drives the SVG brush-size circle that
  // tracks the cursor — matches the main editor's brush cursor
  // pattern (see CanvasArea.tsx). The radius is brush-size × zoom
  // because the displayed brush radius scales with the preview's
  // visible scale, not its internal pixel grid.
  const [brushCursor, setBrushCursor] = useState<{ x: number; y: number; visible: boolean }>({
    x: 0,
    y: 0,
    visible: false,
  });

  // Zoom / pan state for the preview viewport. Required for tight
  // selection refinement on hairlines, eyelashes, etc. — at the
  // default zoom (preview ≤480px longest) a one-source-pixel brush
  // is far too small to land precisely.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // Space held = pan-mode active. Tracked at window level so it
  // works regardless of which element has focus inside the
  // workspace. Active mouse-button drag for pan stored separately
  // so a stroke + pan can't interleave mid-gesture.
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panDragRef = useRef<{
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  // Seed the base binary mask from the initial canvas (if any).
  useEffect(() => {
    // Source is already a canvas — use directly (avoids the
    // putImageData round-trip that froze the modal at 4K+).
    sourceCanvasRef.current = source;

    if (initialMaskCanvas) {
      // Normalize to source dims if the seed mask has different
      // dimensions (the underlying selection helpers always emit
      // image-sized masks, so in practice this is a no-op — but
      // dim normalization is cheap insurance for future callers).
      let maskAtSourceDims = initialMaskCanvas;
      if (initialMaskCanvas.width !== source.width || initialMaskCanvas.height !== source.height) {
        const tmp = document.createElement('canvas');
        tmp.width = source.width;
        tmp.height = source.height;
        const ctx = tmp.getContext('2d', { willReadFrequently: true });
        if (ctx) {
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(initialMaskCanvas, 0, 0, source.width, source.height);
          maskAtSourceDims = tmp;
        }
      }
      const { mask } = canvasToBinaryMask(maskAtSourceDims);
      baseBinaryRef.current = mask;
    } else {
      baseBinaryRef.current = new Uint8Array(source.width * source.height);
    }
  }, [source, initialMaskCanvas]);

  // Fit-to-box dims for the preview canvas.
  const previewDims = useMemo(() => {
    const longest = Math.max(source.width, source.height);
    const scale = longest > PREVIEW_MAX ? PREVIEW_MAX / longest : 1;
    return {
      width: Math.round(source.width * scale),
      height: Math.round(source.height * scale),
      scale,
    };
  }, [source]);

  /** Compute the refined alpha-mask canvas at full source dims by
   *  threading the base binary mask through Smart Radius → Smooth →
   *  Feather → Contrast → Shift Edge in order. Returns a fresh
   *  canvas each call (callers can rebuild the visual after). */
  const computeRefinedMask = (): HTMLCanvasElement => {
    const base = baseBinaryRef.current;
    if (!base) {
      const empty = document.createElement('canvas');
      empty.width = source.width;
      empty.height = source.height;
      return empty;
    }
    const w = source.width;
    const h = source.height;
    // 1. Shift edge first (cheap, binary domain).
    let binMask = shift !== 0 ? shiftEdge(base, w, h, shift) : new Uint8Array(base);
    // 2. Smooth (binary majority pass).
    if (smooth > 0) binMask = smoothMask(binMask, w, h, smooth);
    // 3. Radius/Smart Radius → alpha canvas.
    let alphaCanvas: HTMLCanvasElement;
    if (radius > 0) {
      alphaCanvas = smartRadius
        ? smartRadiusFeather(binMask, w, h, radius)
        : featherMaskToCanvas(binMask, w, h, radius);
    } else {
      alphaCanvas = featherMaskToCanvas(binMask, w, h, 0);
    }
    // 4. Feather (Gaussian alpha blur).
    if (feather > 0) {
      const ctx = alphaCanvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        ctx.filter = `blur(${feather}px)`;
        const tmp = document.createElement('canvas');
        tmp.width = w;
        tmp.height = h;
        tmp.getContext('2d')?.drawImage(alphaCanvas, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(tmp, 0, 0);
        ctx.filter = 'none';
      }
    }
    // 5. Contrast (alpha S-curve).
    if (contrast > 0) {
      alphaCanvas = contrastMaskCanvas(alphaCanvas, contrast);
    }
    return alphaCanvas;
  };

  // Recompute on any input change. For a single-frame slider drag
  // the work is O(W·H) per slider, dominated by canvasToBinary +
  // blur + putImageData on 4K (~50-100ms typical). For interactive
  // dragging we accept the cost in v1; deferred optimization is
  // downsample-during-drag, full-res on commit (same pattern as
  // adjustments).
  // Defer initial draw to the next animation frame. On first
  // open, the workspace mounts inside a Radix Portal — the
  // canvas's drawing surface isn't fully wired up when the
  // effect runs synchronously, leaving a blank preview until
  // the next state change triggers a re-fire. rAF guarantees
  // the DOM has settled. (Same fix Color Range needed.)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const preview = previewCanvasRef.current;
      const src = sourceCanvasRef.current;
      if (!preview || !src) return;
      const refined = computeRefinedMask();
      const pctx = preview.getContext('2d');
      if (!pctx) return;
      pctx.clearRect(0, 0, preview.width, preview.height);

      const pw = preview.width;
      const ph = preview.height;
      if (view === 'on-black') {
        pctx.fillStyle = '#000';
        pctx.fillRect(0, 0, pw, ph);
        // Source × refined alpha (matted on black).
        const matted = matteSource(src, refined, source.width, source.height);
        pctx.drawImage(matted, 0, 0, pw, ph);
      } else if (view === 'on-white') {
        pctx.fillStyle = '#fff';
        pctx.fillRect(0, 0, pw, ph);
        const matted = matteSource(src, refined, source.width, source.height);
        pctx.drawImage(matted, 0, 0, pw, ph);
      } else if (view === 'b-and-w') {
        pctx.fillStyle = '#000';
        pctx.fillRect(0, 0, pw, ph);
        // White-on-black mask render.
        pctx.save();
        pctx.fillStyle = '#fff';
        pctx.fillRect(0, 0, pw, ph);
        pctx.globalCompositeOperation = 'destination-in';
        pctx.drawImage(refined, 0, 0, pw, ph);
        pctx.restore();
      } else if (view === 'on-layers') {
        // Show what the selection would look like as a layer mask
        // over a transparent layer — checkerboard background +
        // matted source on top. Distinct from Overlay (which keeps
        // the un-selected pixels visible under a red tint) and from
        // On Black/White (which just substitutes a solid background).
        drawCheckerboard(pctx, pw, ph);
        const matted = matteSource(src, refined, source.width, source.height);
        pctx.drawImage(matted, 0, 0, pw, ph);
      } else if (view === 'marching-ants') {
        // Source with an animated stippled boundary trace. We extract
        // the mask's binary boundary at preview resolution, then
        // composite a white-dot pattern on top of the source. The
        // `antsPhase` shifts the stipple offset every ~120ms via the
        // setInterval ticker above — gives the visible "march" the
        // user expects from the view-mode label (and ships beyond
        // the main editor's static dashed marquee).
        pctx.drawImage(src, 0, 0, pw, ph);
        const stipple = buildMaskBoundaryStipple(refined, pw, ph, antsPhase);
        pctx.drawImage(stipple, 0, 0);
      } else {
        // 'overlay' (default) — source + red 50% over un-masked.
        pctx.drawImage(src, 0, 0, pw, ph);
        // Build a tinted overlay: red where mask is LOW (un-selected).
        const overlay = document.createElement('canvas');
        overlay.width = source.width;
        overlay.height = source.height;
        const octx = overlay.getContext('2d', { willReadFrequently: true });
        if (octx) {
          octx.fillStyle = '#ff3030';
          octx.fillRect(0, 0, source.width, source.height);
          // Subtract the refined mask's alpha so the red disappears
          // where the user selected.
          octx.globalCompositeOperation = 'destination-out';
          octx.drawImage(refined, 0, 0);
        }
        pctx.save();
        pctx.globalAlpha = 0.5;
        pctx.drawImage(overlay, 0, 0, pw, ph);
        pctx.restore();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [
    view,
    source,
    radius,
    smartRadius,
    smooth,
    feather,
    contrast,
    shift,
    recomputeTick,
    antsPhase,
  ]);

  /** Paint a circular dab into the base binary mask at scene
   *  coords (x, y). Add tool sets pixels to 1; subtract tool clears
   *  them. Simple disk (no falloff — we're editing a binary mask
   *  here; soft edges come from the Feather slider). */
  const paintDab = (x: number, y: number) => {
    const base = baseBinaryRef.current;
    if (!base) return;
    const radiusPx = brushSize / 2;
    const w = source.width;
    const h = source.height;
    const x0 = Math.max(0, Math.floor(x - radiusPx));
    const x1 = Math.min(w - 1, Math.ceil(x + radiusPx));
    const y0 = Math.max(0, Math.floor(y - radiusPx));
    const y1 = Math.min(h - 1, Math.ceil(y + radiusPx));
    const r2 = radiusPx * radiusPx;
    const setBit = tool === 'add' ? 1 : 0;
    for (let yy = y0; yy <= y1; yy++) {
      const dy = yy - y;
      for (let xx = x0; xx <= x1; xx++) {
        const dx = xx - x;
        if (dx * dx + dy * dy <= r2) {
          base[yy * w + xx] = setBit;
        }
      }
    }
  };

  /** Convert preview-space click to source-space coords. The
   *  canvas's `getBoundingClientRect()` already reflects the CSS
   *  `transform: scale(zoom)` we apply for zoom, so the
   *  (clientX-rect.left)/rect.width ratio maps correctly to canvas
   *  internal pixels and then up to source pixels without any
   *  additional zoom math here. */
  const previewToSource = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * source.width;
    const py = ((e.clientY - rect.top) / rect.height) * source.height;
    return { x: px, y: py };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Hand tool and Space-pan both block brush strokes — the
    // outer pan handler picks up the mouse event instead. Using
    // `panModeActive` keeps the two gates in lockstep.
    if (tool === 'hand' || spaceHeld) return;
    const p = previewToSource(e);
    strokeRef.current = { lastX: p.x, lastY: p.y };
    paintDab(p.x, p.y);
    triggerRecompute();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'hand' || spaceHeld || !strokeRef.current) return;
    const p = previewToSource(e);
    const dx = p.x - strokeRef.current.lastX;
    const dy = p.y - strokeRef.current.lastY;
    const dist = Math.hypot(dx, dy);
    const spacing = Math.max(1, brushSize / 4);
    if (dist < spacing) return;
    const steps = Math.ceil(dist / spacing);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      paintDab(strokeRef.current.lastX + dx * t, strokeRef.current.lastY + dy * t);
    }
    strokeRef.current.lastX = p.x;
    strokeRef.current.lastY = p.y;
    triggerRecompute();
  };

  // ─── Zoom / pan ─────────────────────────────────────────────
  /** Cursor-anchored zoom — the canvas pixel under the cursor
   *  stays under the cursor across zoom levels. Math:
   *    cursorRelCenter = cursorX - containerCenterX
   *    newPan = cursorRelCenter - (newZoom / zoom) * (cursorRelCenter - pan)
   *  That's the inverse of the forward transform
   *    screen = containerCenter + pan + (cx - canvasCenter) * zoom
   *  solved for newPan such that the same canvas-internal pixel
   *  lands at the same screen pos before and after the zoom step. */
  const applyZoomAtCursor = useCallback(
    (newZoom: number, clientX: number, clientY: number, rect: DOMRect) => {
      const clamped = Math.max(0.25, Math.min(8, newZoom));
      if (clamped === zoom) return;
      const cxRel = clientX - rect.left - rect.width / 2;
      const cyRel = clientY - rect.top - rect.height / 2;
      const ratio = clamped / zoom;
      setPan({
        x: cxRel - ratio * (cxRel - pan.x),
        y: cyRel - ratio * (cyRel - pan.y),
      });
      setZoom(clamped);
    },
    [zoom, pan],
  );

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Exponential zoom curve — feels linear in perceived scale. The
    // -e.deltaY sign matches the convention "wheel up = zoom in"
    // (which is itself the convention of every other image editor).
    const factor = Math.exp(-e.deltaY * 0.002);
    applyZoomAtCursor(zoom * factor, e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
  };

  /** Pan-mode active when either Space is held OR the Hand mini-
   *  palette tool is selected. The two paths coexist because some
   *  users prefer the modal tool (Hand) while others prefer the
   *  spring-loaded modifier (Space) — Photoshop ships both. */
  const panModeActive = spaceHeld || tool === 'hand';

  const handlePanStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!panModeActive) return;
    e.preventDefault();
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  };

  const handlePanMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const drag = panDragRef.current;
    if (!drag) return;
    setPan({
      x: drag.startPanX + (e.clientX - drag.startX),
      y: drag.startPanY + (e.clientY - drag.startY),
    });
  };

  const handlePanEnd = () => {
    panDragRef.current = null;
  };

  // Space-key tracking — held = pan mode. Suppressed when typing
  // into an input (the workspace has number sliders that might
  // accept space; we don't want to swallow the keystroke there).
  useEffect(() => {
    const isFormElement = (el: EventTarget | null) =>
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isFormElement(e.target)) {
        setSpaceHeld(true);
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpaceHeld(false);
        panDragRef.current = null;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseUp = () => {
    strokeRef.current = null;
  };

  /** Cheap re-render trigger — `radius` is in the dep array of the
   *  recompute effect, but we want the brush stroke to also kick a
   *  recompute. Bumping a no-op state ticker is the simplest path
   *  that doesn't require restructuring the effect. */
  const triggerRecompute = () => setRecomputeTick((t) => t + 1);

  const handleApply = () => {
    onApply(computeRefinedMask(), output);
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 flex flex-col bg-background">
          {/* Top toolbar — view-mode selector centered, Done/Cancel
              on the right. Replaces the editor's normal top toolbar
              while the workspace is open. */}
          <div className="flex items-center justify-between border-b border-border px-4 py-2 bg-background">
            <div className="flex items-center gap-2">
              <DialogPrimitive.Title className="text-sm font-semibold">
                Refine Edge
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Full-screen workspace for refining the current selection. Use the brushes to add or
                subtract from the mask, adjust the sliders to refine edges, and choose an output
                target before applying.
              </DialogPrimitive.Description>
            </div>
            <div className="flex items-center gap-1">
              {VIEW_MODES.map((vm) => (
                <button
                  key={vm.value}
                  type="button"
                  onClick={() => setView(vm.value)}
                  className={`px-2 py-1 text-[11px] rounded ${
                    view === vm.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {vm.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded border border-border text-xs hover:bg-accent"
                title="Cancel"
              >
                <X className="h-4 w-4 inline-block" />
                <span className="sr-only">Cancel</span>
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs hover:opacity-90"
                title="Done — apply refined selection"
              >
                <Check className="h-4 w-4 inline-block mr-1" />
                Done
              </button>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Mini tool palette on the left. */}
            <div className="w-12 border-r border-border bg-background flex flex-col items-center py-2 gap-1">
              <button
                type="button"
                onClick={() => setTool('add')}
                className={`w-9 h-9 flex items-center justify-center rounded ${
                  tool === 'add' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                }`}
                title="Quick Select brush — add to selection"
              >
                <Brush className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setTool('subtract')}
                className={`w-9 h-9 flex items-center justify-center rounded ${
                  tool === 'subtract' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                }`}
                title="Refine Edge brush — subtract from selection"
              >
                <Eraser className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setTool('hand')}
                className={`w-9 h-9 flex items-center justify-center rounded ${
                  tool === 'hand' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                }`}
                title="Hand — drag to pan the preview"
              >
                <Hand className="h-4 w-4" />
              </button>
            </div>

            {/* Preview area — zoomable + pannable. The viewport
                clips overflow so a zoomed-in canvas doesn't push
                the right panel off-screen; the inner wrapper
                carries the transform so brush handlers on the
                canvas still see correct hit-coords via
                getBoundingClientRect (which reflects the post-
                transform rect). */}
            <div
              className="flex-1 relative overflow-hidden bg-[#1a1a1a] select-none"
              onWheel={handleWheel}
              onMouseDown={handlePanStart}
              onMouseMove={(e) => {
                handlePanMove(e);
                // Track cursor for the brush-size circle. Stored in
                // wrapper-relative coords so the SVG overlay (which
                // is also wrapper-anchored) positions correctly
                // regardless of zoom — the size scales by `zoom`
                // while the position lives in raw DOM px.
                const rect = e.currentTarget.getBoundingClientRect();
                setBrushCursor({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                  visible: true,
                });
              }}
              onMouseUp={handlePanEnd}
              onMouseLeave={() => {
                handlePanEnd();
                setBrushCursor((c) => ({ ...c, visible: false }));
              }}
              onMouseEnter={() => setBrushCursor((c) => ({ ...c, visible: true }))}
              style={{
                // The brush-cursor SVG overlay is the visible
                // cursor when a brush tool is active — hide the
                // OS cursor so they don't overlap. (The `tool ===
                // 'hand'` branch can't be reached here: if tool is
                // 'hand', `panModeActive` is true.)
                cursor: panModeActive ? (panDragRef.current ? 'grabbing' : 'grab') : 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center',
                  // Preserve crisp pixel boundaries at high zoom —
                  // the preview canvas itself is small (≤480px),
                  // so we want nearest-neighbor when scaled up.
                  imageRendering: 'pixelated',
                }}
              >
                <canvas
                  ref={previewCanvasRef}
                  width={previewDims.width}
                  height={previewDims.height}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className={
                    panModeActive
                      ? panDragRef.current
                        ? 'cursor-grabbing'
                        : 'cursor-grab'
                      : 'cursor-crosshair'
                  }
                  style={{
                    imageRendering: 'pixelated',
                    display: 'block',
                  }}
                />
              </div>

              {/* Brush-size cursor — SVG circle following the
                  pointer. Hidden in pan mode (Space / Hand) and
                  when the cursor's outside the preview area. Same
                  pattern as the main editor's CanvasArea brush
                  cursor. The displayed radius is `brushSize/2 *
                  zoom` because the preview is rendered at
                  `previewDims.scale × zoom` of source; brush size
                  is in source pixels, so the visible radius scales
                  by the same factor — preview.scale is baked into
                  the canvas dim, then zoom is the additional CSS
                  transform. */}
              {/* `!panModeActive` already implies `tool !== 'hand'`
                  (panModeActive is the OR of spaceHeld + hand), so
                  the cursor only renders when an add/subtract
                  brush is active and the pointer is over the
                  preview area. */}
              {brushCursor.visible && !panModeActive && (
                <svg
                  className="absolute pointer-events-none"
                  style={{
                    left: brushCursor.x - (brushSize / 2) * previewDims.scale * zoom,
                    top: brushCursor.y - (brushSize / 2) * previewDims.scale * zoom,
                    width: brushSize * previewDims.scale * zoom,
                    height: brushSize * previewDims.scale * zoom,
                  }}
                  viewBox="0 0 100 100"
                >
                  {/* Double-stroked outline: black outer + white inner
                      so the cursor stays legible against any
                      background (matches the main editor). */}
                  <circle cx="50" cy="50" r="48" fill="none" stroke="black" strokeWidth="3" />
                  <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="1.5" />
                </svg>
              )}

              {/* Zoom controls — bottom-right, semi-transparent so
                  they don't compete with the preview. Wheel-zoom
                  + space-drag pan handle most cases; these buttons
                  cover discoverability and trackpad users. */}
              <div className="absolute bottom-3 right-3 flex items-center gap-0.5 bg-background/90 border border-border rounded p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() =>
                    applyZoomAtCursor(
                      zoom / 1.25,
                      window.innerWidth / 2,
                      window.innerHeight / 2,
                      // Fake a rect centered in the window — zoom
                      // anchored to current pan center for buttons.
                      new DOMRect(0, 0, window.innerWidth, window.innerHeight),
                    )
                  }
                  className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent"
                  title="Zoom out"
                  aria-label="Zoom out"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={resetZoom}
                  className="px-2 h-7 flex items-center justify-center rounded hover:bg-accent tabular-nums min-w-[3.5rem]"
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={() =>
                    applyZoomAtCursor(
                      zoom * 1.25,
                      window.innerWidth / 2,
                      window.innerHeight / 2,
                      new DOMRect(0, 0, window.innerWidth, window.innerHeight),
                    )
                  }
                  className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent"
                  title="Zoom in"
                  aria-label="Zoom in"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Hint text — only shown briefly on first open
                  could be nice, but for now ship as a permanent
                  bottom-left affordance so users discover the
                  shortcuts. */}
              <div className="absolute bottom-3 left-3 text-[10px] text-muted-foreground bg-background/80 border border-border rounded px-2 py-1 pointer-events-none">
                Wheel to zoom · hold Space + drag to pan
              </div>
            </div>

            {/* Right-side panel. */}
            <div className="w-72 border-l border-border bg-background overflow-y-auto">
              <div className="p-4 space-y-5 text-xs">
                {/* Brush size */}
                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">Brush</h4>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Size</span>
                    <input
                      type="range"
                      min={1}
                      max={200}
                      value={brushSize}
                      onChange={(e) => setBrushSize(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{brushSize}</span>
                  </label>
                </section>

                {/* Edge detection */}
                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">Edge Detection</h4>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Radius</span>
                    <input
                      type="range"
                      min={0}
                      max={50}
                      value={radius}
                      onChange={(e) => setRadius(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{radius}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={smartRadius}
                      onChange={(e) => setSmartRadius(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    <span>Smart Radius</span>
                  </label>
                </section>

                {/* Global refinements */}
                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">
                    Global Refinements
                  </h4>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Smooth</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={smooth}
                      onChange={(e) => setSmooth(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{smooth}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Feather</span>
                    <input
                      type="range"
                      min={0}
                      max={50}
                      value={feather}
                      onChange={(e) => setFeather(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{feather}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Contrast</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={contrast}
                      onChange={(e) => setContrast(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{contrast}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Shift Edge</span>
                    <input
                      type="range"
                      min={-20}
                      max={20}
                      value={shift}
                      onChange={(e) => setShift(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{shift}</span>
                  </label>
                </section>

                {/* Output destination. */}
                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">Output</h4>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground w-16">Output To</span>
                    <select
                      value={output}
                      onChange={(e) => setOutput(e.target.value as RefineEdgeOutput)}
                      className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs"
                    >
                      <option value="selection">Selection</option>
                      {/* Other outputs are reserved in the roadmap but
                          deferred — see EditorPage's onApply comment. */}
                      <option value="mask" disabled>
                        Layer Mask (soon)
                      </option>
                      <option value="new-layer" disabled>
                        New Layer (soon)
                      </option>
                      <option value="new-layer-with-mask" disabled>
                        New Layer with Mask (soon)
                      </option>
                    </select>
                  </label>
                </section>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Build a matte canvas: source pixels multiplied by the alpha
 *  mask, so the preview shows only the selected region with the
 *  rest transparent. Used by On Black / On White / On Layers
 *  view modes. */
function matteSource(
  src: HTMLCanvasElement,
  alphaMask: HTMLCanvasElement,
  width: number,
  height: number,
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(src, 0, 0, width, height);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(alphaMask, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

/** Paint a standard 8×8 transparency checkerboard onto the given
 *  context, sized to (w, h). Used by the "On Layers" view mode to
 *  simulate what's behind the selection if it were applied as a
 *  layer mask over a transparent layer. */
function drawCheckerboard(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const tile = 8;
  ctx.fillStyle = '#4d4d4d';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#737373';
  for (let y = 0; y < h; y += tile) {
    for (let x = 0; x < w; x += tile) {
      if ((x / tile + y / tile) % 2 === 0) {
        ctx.fillRect(x, y, tile, tile);
      }
    }
  }
}

/** Extract the binary mask's boundary at the target preview
 *  dimensions and return a stipple canvas suitable for compositing
 *  over the source preview. Pixels are "boundary" if they're
 *  inside the mask AND any 4-connected neighbor is outside. The
 *  stipple pattern `((x+y+phase) >> 1) % 2 === 0` shifts each
 *  phase tick — incrementing `phase` over time produces the
 *  classic "marching ants" motion along the boundary.
 *
 *  Runs at preview resolution (≤480px longest), so cost is bounded
 *  even on 8K sources. */
function buildMaskBoundaryStipple(
  alphaMask: HTMLCanvasElement,
  destW: number,
  destH: number,
  phase: number = 0,
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = destW;
  out.height = destH;
  const octx = out.getContext('2d', { willReadFrequently: true });
  if (!octx) return out;
  // Downscale the full-source mask to preview dims first — we want
  // boundary detection in display-pixel space, not source-pixel
  // space (otherwise a 4K source at 480-preview produces sub-pixel
  // boundaries that smear after the downscale).
  octx.imageSmoothingEnabled = false;
  octx.drawImage(alphaMask, 0, 0, destW, destH);
  const id = octx.getImageData(0, 0, destW, destH);
  const d = id.data;
  const stipple = octx.createImageData(destW, destH);
  const sd = stipple.data;
  for (let y = 0; y < destH; y++) {
    for (let x = 0; x < destW; x++) {
      const i = (y * destW + x) * 4;
      const a = d[i + 3];
      if (a < 128) continue;
      // 4-connectivity neighbors (clamped at edges).
      const left = x > 0 ? d[i - 4 + 3] : 0;
      const right = x < destW - 1 ? d[i + 4 + 3] : 0;
      const up = y > 0 ? d[i - destW * 4 + 3] : 0;
      const down = y < destH - 1 ? d[i + destW * 4 + 3] : 0;
      const isBoundary = left < 128 || right < 128 || up < 128 || down < 128;
      if (isBoundary && (((x + y + phase) >> 1) & 1) === 0) {
        sd[i] = 255;
        sd[i + 1] = 255;
        sd[i + 2] = 255;
        sd[i + 3] = 255;
      }
    }
  }
  octx.putImageData(stipple, 0, 0);
  return out;
}
